// Downloads are per account (server + user): two accounts may each download
// the same item. A record is identified by `downloadKey`; its files live in
// the OPFS folder `downloads/<folder>`.

export interface DownloadAccount {
  serverUrl: string;
  userId: string;
}

/** `serverUrl|userId|itemId` (item ids never contain `|`). */
export function downloadKey(account: DownloadAccount, itemId: string): string {
  return `${account.serverUrl}|${account.userId}|${itemId}`;
}

export function isDownloadKey(value: string): boolean {
  return value.includes('|');
}

/** cyrb53: a short, stable account tag for folder names. */
function hash53(text: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

/**
 * OPFS folder for a new download: `<itemId>~<account tag>`. Records made
 * before downloads were per account keep their `<itemId>` folder.
 */
export function downloadFolderFor(account: DownloadAccount, itemId: string): string {
  return `${itemId.replace(/[^\w.-]/g, '_')}~${hash53(`${account.serverUrl}|${account.userId}`)}`;
}

export function belongsTo(record: DownloadAccount, account: DownloadAccount | null | undefined): boolean {
  return !!account && record.serverUrl === account.serverUrl && record.userId === account.userId;
}
